import get from 'lodash/get'
import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { useQuery } from '../../../api/graphql'
import OperatingHoursModel from '../../../api/places/operatingHoursModel'
import Routes from '../../../startup/routes'
import { PlaceViewQuery } from '../../containers/PlaceViewContainer'
import Loading from '../misc/Loading'
import { mediaSrc } from '../misc/MediaPresets'
import { IconGlyph } from '../../../icons/IconGlyph'

type OperatingHoursProps = { operatingHours: OperatingHoursModel }
const OperatingHours: React.FC<OperatingHoursProps> = ({ operatingHours }) => {
  const openCloseTimes = operatingHours.openCloseTimesToday()
  // this is an edge case when all operatingHours days open/close time are null
  // this means that operatingHours has not been set at all, rather than that the place is closed
  // so we don't show the place as being closed in this case:
  // operatingHours: { monday: { openTime: null, closeTime: null }, tuesday: { openTime: null, closeTime: null }, ...
  if (!operatingHours.isOpenNow && (!openCloseTimes || !openCloseTimes.openTime || !openCloseTimes.closeTime)) {
    return null
  }
  return (
    <p className="tooltip-hours">
      {operatingHours.isOpenNow ? (
        <>
          <strong className="u-green">Open now</strong>
          {` until ${operatingHours.closingTimeLabel}`}
        </>
      ) : (
        <strong className="u-red">Closed now</strong>
      )}
    </p>
  )
}

type Props = { placeSlug: string; communityIdParam?: string; closePopup: () => void }
const PlacePopup: React.FC<Props> = ({ placeSlug, communityIdParam, closePopup }) => {
  const { loading, data: { place = null } = {} } = useQuery(PlaceViewQuery, {
    variables: { placeSlug },
    skip: !placeSlug,
    // A pop-up on the places map shows no "Server error" note (CVL-145)
    context: { silentErrors: true },
  })
  // A premium place whose photo can't load would lose the "View page" link drawn on the photo
  const [failedPhotoId, setFailedPhotoId] = useState<string | null>(null)

  const directionsUrl = useCallback(() => {
    const coordinates = get(place, 'geo.geometry.coordinates')
    if (coordinates) {
      const [lng, lat] = coordinates
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    }
    // A place saved without a location is searched by its address instead
    const { streetAddress, city, state, zipCode } = place.address || {}
    const address = [streetAddress, city, state, zipCode].filter(Boolean).join(', ')
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  }, [place])

  const renderContactPhone = () => {
    if (!get(place, 'contact.phone')) return null

    return (
      <p>
        <a href={`tel:${place.contact.phone}`} className="bulletIcon">
          <IconGlyph name="call" color="red" />
          {place.contact.phone}
        </a>
      </p>
    )
  }

  if (loading) return <Loading className="isDark" />

  const pageUrl = place.isPremium ? Routes.premiumPlace(place, communityIdParam) : null
  const photoFailed = place.isPremium && failedPhotoId === place.mainImage.publicId

  return (
    <article className="tooltip" id={`popup-${place.id}`}>
      <header className="tooltip-header">
        <h2 className="tooltip-header-title">{place.name}</h2>
        <button className="close-tooltip" onClick={() => closePopup()}>
          <IconGlyph name="cancel" />
        </button>
      </header>

      {place.isPremium && !photoFailed && (
        <Link to={pageUrl} className="tooltip-image">
          <img
            src={mediaSrc({ ...place.mainImage, dpr: '2.0', width: '330', height: '150' })}
            alt=""
            onError={() => setFailedPhotoId(place.mainImage.publicId)}
          />
          <div className="tooltip-image-overlay">
            <div className="tooltip-image-overlay-text">
              View page <IconGlyph name="arrow-right-long" />
            </div>
          </div>
        </Link>
      )}

      <div className="tooltip-details">
        <p className="tooltip-description">{place.shortDescription}</p>
        <div className="tooltip-cols">
          <div className="tooltip-details-col">
            <OperatingHours operatingHours={place.operatingHours} />
            {renderContactPhone()}
            <p>
              <a className="bulletIcon" href={directionsUrl()} rel="noopener noreferrer" target="_blank">
                <IconGlyph name="location" color="green" />
                {place.address.streetAddress} <br /> {place.address.city}
              </a>
            </p>
          </div>
          {(place.webUrl || photoFailed) && (
            <div className="tooltip-details-col">
              {photoFailed && (
                <p>
                  <Link to={pageUrl} className="btn btn-darkblue" style={{ textTransform: 'capitalize' }}>
                    View page <IconGlyph name="arrow-right-long" />
                  </Link>
                </p>
              )}
              {place.webUrl && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={place.webUrl}
                  className="btn btn-darkblue"
                  style={{ textTransform: 'capitalize' }}
                >
                  Visit Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

export default PlacePopup
